import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { createMaterialLibrary } from '../scene/materials';
import { createStudio } from './studio';
import { makeCustomDoor, disposeModel } from './model';
import { slidingHandleStatus } from '../scene/slidingHandle';
import { geometryKey } from './rules';
import { geometryStatus } from './geometryStatus';
import { createPhotoScene } from '../photo/photoScene';

export function createConfiguratorEngine(host, onReady, onError, onPlacement) {
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  const gl = renderer.getContext(), info = gl.getExtension('WEBGL_debug_renderer_info');
  const software = !info || /swiftshader|llvmpipe|software/i.test(gl.getParameter(info.UNMASKED_RENDERER_WEBGL));
  renderer.userData = { software }; renderer.setPixelRatio(software ? .75 : Math.min(window.devicePixelRatio, 1.5));
  renderer.toneMapping = THREE.ACESFilmicToneMapping; renderer.toneMappingExposure = 1.1;
  renderer.shadowMap.enabled = !software; renderer.shadowMap.type = THREE.PCFShadowMap;
  renderer.shadowMap.autoUpdate = false;
  const canvas = renderer.domElement; canvas.dataset.testid = 'canvas-configurator'; canvas.setAttribute('aria-label', 'Your ViewLux product preview'); canvas.tabIndex = 0; host.appendChild(canvas);
  const scene = new THREE.Scene(), camera = new THREE.PerspectiveCamera(42, 1, .02, 1000);
  const controls = new OrbitControls(camera, canvas); controls.enableDamping = true; controls.enablePan = true; controls.maxPolarAngle = Math.PI * .49;
  let dirty = true, stopped = false, raf, lastRender = 0, model = null, design = null, key = '', finishId = '', ready = false;
  let openings = { a: 0, b: 0 }, screens = { a: 0, b: 0 }, transitions = {};
  const library = createMaterialLibrary(renderer, () => { dirty = true; }), frame = library.makeFrame();
  const studio = createStudio(scene, renderer, library);
  const photo = createPhotoScene({ renderer, scene, camera, controls, studio, host, getModel: () => model, onChange: onPlacement });
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  controls.addEventListener('change', () => { dirty = true; });
  const fit = (view = 'perspective') => {
    if (!model) return;
    if (photo.active) { photo.fit(); dirty = true; return; }
    model.group.updateMatrixWorld(true);
    const bounds = new THREE.Box3().setFromObject(model.group), sphere = bounds.getBoundingSphere(new THREE.Sphere());
    const halfFov = THREE.MathUtils.degToRad(camera.fov / 2), horizontal = Math.atan(Math.tan(halfFov) * camera.aspect);
    const distance = sphere.radius / Math.sin(Math.min(halfFov, horizontal)) * 1.13;
    const direction = view === 'top' ? new THREE.Vector3(.01, 1, .01) : view === 'front' && design.layout === 'straight' ? new THREE.Vector3(0, .08, 1) : new THREE.Vector3(design.layout === 'corner' ? 1 : .48, .42, 1);
    controls.target.copy(sphere.center); camera.position.copy(sphere.center).add(direction.normalize().multiplyScalar(distance));
    controls.minDistance = Math.max(.35, sphere.radius * .28); controls.maxDistance = Math.max(12, distance * 3);
    camera.lookAt(controls.target); controls.update(); studio.fit(sphere.center, Math.max(3, sphere.radius * 1.2)); dirty = true;
  };
  const resize = () => {
    const { width, height } = host.getBoundingClientRect(); if (!width || !height) return;
    renderer.setSize(width, height); camera.aspect = width / height; camera.updateProjectionMatrix(); fit(); dirty = true;
  };
  const observer = new ResizeObserver(resize); observer.observe(host); resize();
  const contextLost = event => { event.preventDefault(); onError('The 3D graphics connection was interrupted. Reopen the preview to continue.'); };
  canvas.addEventListener('webglcontextlost', contextLost);
  const render = () => {
    photo.beforeRender(); renderer.shadowMap.needsUpdate = true; renderer.render(scene, camera); dirty = false; lastRender = performance.now();
    if (!design || !model) return;
    Object.assign(canvas.dataset, {
      ready: 'true', layout: design.layout, model: design.model_id, finish: finishId,
      backdrop: studio.backdropMode,
      widthA: String(design.run_a.width_in), widthB: String(design.run_b?.width_in || 0), heightIn: String(design.height_in),
      panelsA: String(model.runs[0].count), panelsB: String(model.runs[1]?.count || 0), tracks: String(model.runs[0].tracks),
      swingFrameCode: model.runs[0].group.userData.frameCode || '', swingLeafCode: model.runs[0].group.userData.leafCode || '',
      swingLeafDepthMm: String(model.runs[0].group.userData.leafDepthMm || 0), swingFrameDepthMm: String(model.runs[0].group.userData.frameDepthMm || 0),
      swingFrameFaceMm: String(model.runs[0].group.userData.frameFaceMm || 0), swingSightlineMm: String(model.runs[0].group.userData.sideSightlineMm || 0),
      swingAngle: String(model.runs[0].group.userData.swingAngle || 0),
      bifoldLayout: model.runs[0].group.userData.layout || '', bifoldAngle: String(model.runs[0].group.userData.foldAngle || 0),
      bifoldPanelsLeft: String(model.runs[0].group.userData.panelsLeft || 0), bifoldPanelsRight: String(model.runs[0].group.userData.panelsRight || 0),
      pivotOffsetPercent: String(model.runs[0].group.userData.pivotOffsetPercent ?? ''), pivotAxisX: String(model.runs[0].group.userData.pivotAxisX ?? ''),
      pivotDirection: model.runs[0].group.userData.pivotDirection || '', pivotAngle: String(model.runs[0].group.userData.pivotAngle || 0), pivotSignedAngle: String(model.runs[0].group.userData.pivotSignedAngle || 0),
      tracksA: String(model.runs[0].tracks), tracksB: String(model.runs[1]?.tracks || 0),
      railDepthA: String(Math.round(model.runs[0].depth * 1000)), railDepthB: String(Math.round((model.runs[1]?.depth || 0) * 1000)),
      railSourceA: model.runs[0].tracks > (design.model_id === 'sd-150' ? 2 : 3) ? 'conceptual-multi-track' : 'cad-derived',
      railSourceB: !model.runs[1] ? 'none' : model.runs[1].tracks > 3 ? 'conceptual-multi-track' : 'cad-derived',
      openingA: openings.a.toFixed(3), openingB: openings.b.toFixed(3), screenA: screens.a.toFixed(3), screenB: screens.b.toFixed(3),
      pocket: String(design.sd_pocket || design.model_id === 'mt150-pocket'),
      meshEnabled: String(model.runs.some(run => !!run.screen)),
      meshCount: String(model.runs.filter(run => !!run.screen).length),
      meshExtensionA: String(model.runs[0].screen?.root.userData.extension ?? 0),
      meshExtensionB: String(model.runs[1]?.screen?.root.userData.extension ?? 0),
      windowShape: design.window_shape || 'rectangle',
      handlePositions: slidingHandleStatus(model.group),
      camera: camera.position.toArray().map(v => v.toFixed(3)).join(','),
      panelPositions: JSON.stringify(model.runs.map(run => run.group.userData.panelPositions)),
      dividers: JSON.stringify(model.runs[0].group.userData.dividers),
      dividerInstances: String(model.runs.reduce((total, run) => total + (run.group.getObjectByName('glass-lite-dividers')?.count || run.group.getObjectByName('fixed-window-clipped-grilles')?.userData.dividerCount || run.group.getObjectByName('swing-arch-grilles')?.userData.dividerCount || 0), 0)),
      ...geometryStatus(model, design.layout === 'corner'),
      drawCalls: String(renderer.info.render.calls), triangles: String(renderer.info.render.triangles), textureState: library.status,
    });
    if (!ready) { ready = true; onReady(); }
  };
  const tick = now => {
    if (stopped) return;
    raf = requestAnimationFrame(tick); if (!photo.active) controls.update();
    if (model) for (const run of model.runs) {
      for (const [name, current, target] of [['openings', openings, design.openings], ['screens', screens, design.screens]]) {
        const side = run.side, to = target[side] / 100;
        if (current[side] === to) continue;
        const transitionKey = name + side;
        if (!transitions[transitionKey] || transitions[transitionKey].target !== to) transitions[transitionKey] = { from: current[side], target: to, start: now };
        const transition = transitions[transitionKey], t = reduced ? 1 : Math.min((now - transition.start) / 900, 1);
        current[side] = t === 1 ? to : THREE.MathUtils.lerp(transition.from, to, t * t * (3 - 2 * t));
        if (name === 'openings') run.animate(current[side]); else run.screen?.animate(current[side]);
        dirty = true;
      }
    }
    if (dirty && (!software || now - lastRender > 80)) render();
  };
  raf = requestAnimationFrame(tick);
  return {
    update: (next, product, finish, dimensions, photoState = { enabled: false }) => {
      const nextKey = geometryKey(next); design = next;
      canvas.setAttribute('aria-label', `${product.name} preview`);
      if (nextKey !== key) {
        if (model) { scene.remove(model.group); disposeModel(model.group); }
        key = nextKey; model = makeCustomDoor(next, product, library, frame); scene.add(model.group);
        openings = { a: next.openings.a / 100, b: next.openings.b / 100 }; screens = { a: next.screens.a / 100, b: next.screens.b / 100 }; transitions = {};
        fit();
      }
      if (finishId !== finish.id) { library.applyFinish(frame, finish); finishId = finish.id; }
      studio.setFinish(finish);
      model.runs.forEach(run => { run.measures.visible = dimensions && !photoState.enabled; });
      library.setPhotoMode(!!photoState.enabled);
      if (photo.set(photoState)) fit();
      dirty = true; render();
    },
    fit,
    getOpeningRegions: () => photo.openingRegions(),
    capturePhoto: async options => {
      if (options?.reference) model.runs.forEach(run => { run.animate(design.openings[run.side] / 100); run.screen?.animate(design.screens[run.side] / 100); });
      try { return await photo.capture(options); }
      finally { if (options?.reference) { model.runs.forEach(run => { run.animate(openings[run.side]); run.screen?.animate(screens[run.side]); }); dirty = true; } }
    },
    dispose: () => {
      stopped = true; cancelAnimationFrame(raf); observer.disconnect(); photo.dispose(); controls.dispose();
      canvas.removeEventListener('webglcontextlost', contextLost);
      if (model) disposeModel(model.group); studio.dispose(); library.dispose(); renderer.dispose(); renderer.forceContextLoss(); canvas.remove();
    },
  };
}